// C:\Users\phill\paperplay\frontend\src\components\molecules\ScrollTop.tsx

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

type Props = {
  /** Respect anchors only if an element with that id exists. */
  respectAnchors?: boolean;
  /** Optional explicit scroll container (e.g. '#main-content' or '[data-scroll-container]'). */
  containerSelector?: string;
  /** Reset *all* scrollable containers, not just window/doc or containerSelector. */
  aggressive?: boolean;
};

/**
 * Single source of truth for scroll behavior:
 * - If URL has a #hash and an element exists (now or soon), scroll it into view.
 * - Otherwise, snap to top of window and any scrollable containers.
 * - Disables browser's history.scrollRestoration while mounted.
 */
export default function ScrollTop({
  respectAnchors = true,
  containerSelector,
  aggressive = false,
}: Props) {
  const location = useLocation();

  /** Primary scroll container (window/document or an explicit container). */
  const getPrimaryScroller = useCallback((): HTMLElement => {
    if (containerSelector) {
      const el = document.querySelector<HTMLElement>(containerSelector);
      if (el) return el;
    }
    return (document.scrollingElement as HTMLElement) || document.documentElement;
  }, [containerSelector]);

  /** All scrollable containers (aggressive scan when enabled). */
  const getAllScrollers = useCallback((): HTMLElement[] => {
    if (!aggressive) return [getPrimaryScroller()];
    const set = new Set<HTMLElement>();

    // Always include window/doc candidates
    const html = document.documentElement;
    const body = document.body;
    const se = (document.scrollingElement as HTMLElement) || html;
    [se, html, body].forEach(el => set.add(el));

    // Likely app shells
    ['#main-content', '#root', '#app', 'main', '[data-scroll-container]'].forEach(sel => {
      const el = document.querySelector<HTMLElement>(sel);
      if (el) set.add(el);
    });

    // Any element that can scroll (cheap-ish scan)
    document.querySelectorAll<HTMLElement>('body, body *').forEach(el => {
      const s = getComputedStyle(el);
      const canScrollY =
        (s.overflowY === 'auto' ||
          s.overflowY === 'scroll' ||
          s.overflow === 'auto' ||
          s.overflow === 'scroll') &&
        el.scrollHeight > el.clientHeight;
      if (canScrollY) set.add(el);
    });

    return Array.from(set);
  }, [aggressive, getPrimaryScroller]);

  /** Snap to absolute top synchronously; retry a few times for late layout. */
  const snapToTop = useCallback(() => {
    const html = document.documentElement;
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';

    const snap = () => {
      try {
        if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);
      } catch {
        /* ignore */ void 0;
      }

      // Containers
      getAllScrollers().forEach((el: HTMLElement) => {
        try {
          if (typeof el.scrollTo === 'function') el.scrollTo(0, 0);
          el.scrollTop = 0;
          el.scrollLeft = 0;
        } catch {
          /* ignore */ void 0;
        }
      });
    };

    snap();
    requestAnimationFrame(snap);
    setTimeout(snap, 0);
    setTimeout(() => {
      snap();
      html.style.scrollBehavior = prevBehavior;
    }, 60);
  }, [getAllScrollers]);

  // Keep latest ref so effects don't need snapToTop in deps
  const snapRef = useRef(snapToTop);
  useEffect(() => {
    snapRef.current = snapToTop;
  }, [snapToTop]);

  /** Scroll to #hash target if present; retry for lazy-loaded content. */
  const scrollToAnchorWithRetry = useCallback(
    (onFound?: () => void, maxMs = 1200, stepMs = 50): (() => void) => {
      if (!respectAnchors) return () => undefined;
      const hash = location.hash;
      if (!hash) return () => undefined;

      const id = decodeURIComponent(hash.slice(1));
      if (!id) return () => undefined;

      let cancelled = false;
      const scrollNow = (): boolean => {
        const target = document.getElementById(id);
        if (target) {
          try {
            // Honor CSS scroll-margin-top on sections
            target.scrollIntoView({ block: 'start', behavior: 'auto' });
          } catch {
            /* ignore */ void 0;
          }
          onFound?.();
          return true;
        }
        return false;
      };

      // Try immediately; if not present, poll briefly (lazy/Suspense content).
      if (scrollNow()) {
        return () => {
          cancelled = true;
        };
      }

      const start = performance.now();
      let timeoutId: number | undefined;

      const tick = () => {
        if (cancelled) return;
        if (scrollNow()) return;
        if (performance.now() - start >= maxMs) return;
        timeoutId = window.setTimeout(tick, stepMs);
      };

      timeoutId = window.setTimeout(tick, stepMs);

      return () => {
        cancelled = true;
        if (timeoutId !== undefined) clearTimeout(timeoutId);
      };
    },
    [location.hash, respectAnchors],
  );

  /** Take control of browser's scroll restoration & handle bfcache restores. */
  useEffect(() => {
    try {
      if ('scrollRestoration' in window.history) {
        (window.history as History & { scrollRestoration: 'auto' | 'manual' }).scrollRestoration =
          'manual';
      }
    } catch {
      /* ignore */ void 0;
    }

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) snapRef.current();
    };
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
      try {
        if ('scrollRestoration' in window.history) {
          (window.history as History & { scrollRestoration: 'auto' | 'manual' }).scrollRestoration =
            'auto';
        }
      } catch {
        /* ignore */ void 0;
      }
    };
  }, []);

  /**
   * On route (path/search/hash) change:
   * - If a real #hash target exists (now or soon), scroll to it.
   * - Otherwise, snap to top.
   */
  useLayoutEffect(() => {
    // If there is a hash, attempt to honor it with retries; otherwise snap now.
    if (respectAnchors && location.hash) {
      const fallbackTimer: number = window.setTimeout(() => {
        snapRef.current();
      }, 1250);

      // If the anchor shows up, cancel fallback.
      const cancelRetry = scrollToAnchorWithRetry(() => {
        clearTimeout(fallbackTimer);
      });

      return () => {
        clearTimeout(fallbackTimer);
        cancelRetry();
      };
    }

    // No hash -> go to top immediately.
    snapRef.current();
    return undefined;
  }, [location.pathname, location.search, location.hash, respectAnchors, scrollToAnchorWithRetry]);

  return null;
}
